import { useTranslation } from "@i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ImCross } from "react-icons/im";
import { RiBookLine } from "react-icons/ri";
import Measurements from "@Features/viewer-modals/components/Modal/Measurements";
import {
  getTakeoff,
  getDisplayQuantity,
  measurementSources,
  getEstimateNumberValueBasedOnSettings,
} from "@Actions/estimation";
import Measurement from "@Components/StickySidebarViewer/assets/Measurement";
import { convertDate } from "@Services/luxonHelper";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import type { ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";

/** One row of the takeoff items grid: a stored takeoff item keyed by `itemId` for SlickGrid. */
type TakeoffItemRow = NonNullable<TakeoffData["items"]>[number] & { id: string };
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";
import type { EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import type { TakeoffData, TakeoffMeasurement } from "@Helpers/types/estimation/takeoffTypes";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type TakeoffPropertiesProps = {
  takeOffItemId: ObjectIdString;
  closeTakeoffsSidebar: () => void;
  estimateId?: ObjectIdString;
};

function TakeoffProperties({ takeOffItemId, closeTakeoffsSidebar, estimateId }: TakeoffPropertiesProps) {
  const { t } = useTranslation();
  const itemsGrid = useRef(null);
  // Entry point: route params are outside the type system; the backend route schema validates them.
  const params = useParams<{ versionId: ObjectIdString }>();

  const emptyTakeoff: TakeoffData = {
    takeOffItemId: "",
    name: "",
    description: "",
    createdBy: { _id: "" },
    additionalQuantity: 0,
    measurementUnit: {
      id: "",
      name: "",
      factor: 1,
      selectedSymbol: { id: "", name: "" },
      type: { name: "Linear" },
    },
    items: [],
  };

  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const [takeoff, setTakeoff] = useState<TakeoffData>(emptyTakeoff);
  const iconColor = "#192048";

  const handleGetTakeoff = useCallback(async () => {
    if (!estimateId) return;
    const response = await getTakeoff({
      takeOffItemId,
      estimateId,
      versionId: params.versionId,
    });
    if (response.data.result) {
      setTakeoff(response.data.result as TakeoffData);
    }
  }, [takeOffItemId, estimateId, params.versionId]);

  useEffect(() => {
    handleGetTakeoff();
  }, [handleGetTakeoff]);

  const detailsHeader = () => (
    <div className="flex items-center gap-1">
      <IconComponent name="details" width="1.5rem" height="1.5rem" />
      <strong>
        <Text>{t("Details")}</Text>
      </strong>
    </div>
  );

  const itemsHeader = () => (
    <strong>
      <Text>{t("Associated Items")}</Text>
    </strong>
  );

  const takeOffQuantity = (params: TakeoffData) => {
    let totalQuantity = parseFloat(String(params.additionalQuantity ?? 0));
    if (params.measurements?.length) {
      params.measurements.map((measurement: TakeoffMeasurement) => {
        totalQuantity += Number(measurement.quantity) * measurement.multiplier;
      });
    }

    const value = getDisplayQuantity({
      quantity: totalQuantity,
      factor: params.measurementUnit?.factor,
    }).quantity;
    return getEstimateNumberValueBasedOnSettings(value ?? 0, estimateData, "takeoff");
  };

  const measurementsHeader = () => (
    <div className="flex flex-row items-center justify-start">
      <Measurement color={iconColor} size="20" onClick={undefined} />
      <strong>
        <Text>{t("Measurements")}</Text>
      </strong>
    </div>
  );
  const detailsContent = () => (
    <div className="flex flex-col">
      <div className="flex flex-row items-center justify-between">
        <Text className="text-[#666a84]">{t("Name")}</Text>
        <p>{takeoff.name}</p>
      </div>
      <div className="flex flex-row items-center justify-between">
        <Text className="text-[#666a84]">{t("Description")}</Text>
        <p>{takeoff.description}</p>
      </div>
      <div className="flex flex-row items-center justify-between">
        <Text className="text-[#666a84]">{t("Quantity")}</Text>
        <p>{takeOffQuantity(takeoff)}</p>
      </div>
      <div className="flex flex-row items-center justify-between">
        <Text className="text-[#666a84]">{t("Units")}</Text>
        <p>{takeoff.measurementUnit.name}</p>
      </div>
    </div>
  );

  const serialTemplate = (data: number) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<div>{data + 1}</div>);
    return container;
  };

  const codeTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: { code?: string }) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<div>{params.code || "-"}</div>);
    return container;
  };

  const [gridColumns] = useState([
    {
      field: "serial",
      id: "serial",
      name: t("Sr. No"),
      formatter: serialTemplate,
      textAlign: "Left",
      maxWidth: 50,
    },
    {
      field: "code",
      id: "code",
      name: t("Code"),
      maxWidth: 100,
      formatter: codeTemplate,
      textAlign: "Left",
    },

    {
      field: "description",
      id: "description",
      name: t("Description"),
      wrapText: true,
      textAlign: "Left",
    },
  ]);

  const itemsContent = () => (
    <div className="overflow-auto h-60">
      <SlickGrid1
        ref={itemsGrid}
        showPageCount={false}
        columnDefinitions={gridColumns as typeof gridColumns & ValidatedColumnDefinition<TakeoffItemRow>[]}
        rowHeight={40}
        dataset={(takeoff.items ?? []).map((data) => ({ ...data, id: data.itemId }))}
      />
    </div>
  );

  const measurementsContent = () => (
    <div className="overflow-auto h-80">
      <Measurements
        source={measurementSources.TAKEOFF_SIDEBAR}
        takeOffItemId={takeOffItemId}
        estimateId={estimateId}
        takeOffIncludedMeasuremnt
      />
    </div>
  );

  return (
    <div className="fixed top-0 start-0 w-screen h-screen bg-[var(--background-scrim)] z-[12]">
      <div className="fixed end-[0.25%] top-[0.5%] w-[28.125rem] h-[99%] z-[5] bg-white shadow-[0.25rem_0.5rem_1.25rem_0_rgb(0_0_0_/_30%)] overflow-y-auto">
        <div className="pt-[1.875rem] pb-5 px-5">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center justify-start">
              <RiBookLine color={iconColor} size="20" className="me-1" />
              <h3 className="text-lg font-bold">{takeoff.name}</h3>
            </div>
            <ImCross size={12} className="cursor-pointer" onClick={() => closeTakeoffsSidebar()} />
          </div>
          <small className="flex gap-1 items-center">
            <strong>{takeoff.createdBy?.fullName}</strong>
            <Text sx={{ whiteSpace: "normal" }}>{t("Created On")}</Text>
            <strong>{convertDate(Number(takeoff.createdDate), "dateForText")}</strong>
          </small>
        </div>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{detailsHeader()}</AccordionSummary>
          <AccordionDetails>{detailsContent()}</AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{itemsHeader()}</AccordionSummary>
          <AccordionDetails>{itemsContent()}</AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{measurementsHeader()}</AccordionSummary>
          <AccordionDetails>{measurementsContent()}</AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}

export default TakeoffProperties;
